import { AdminApiParams, AdminApiResponse } from '../../types/auth';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

export async function callAdminApi(
  method: string,
  collection: string,
  params: AdminApiParams = {}
): Promise<AdminApiResponse> {
  let requestBody: AdminApiResponse = { method, collection, params };

  // Handle special parameter formats for specific methods
  if (method === 'findOrCreateLabel' && collection === 'labels') {
    const displayName = params.displayName;
    if (!displayName) {
      throw new Error('displayName is required for findOrCreateLabel');
    }
    requestBody = {
      method: method,
      collection: collection,
      params: { displayName }
    };
  } else if (method === 'queryEmailSubscriptions' && collection === 'emailSubscriptions') {
    requestBody = {
      method: method,
      collection: collection,
      params: {}
    };
  } else if (method === 'queryMarketingConsent' && collection === 'marketingConsent') {
    requestBody = {
      method: method,
      collection: collection,
      params: params
    };
  } else if (method === 'querySubscriptions' && collection === 'subscriptions') {
    requestBody = {
      method: method,
      collection: collection,
      params: params
    };
  } else if (method === 'getSubscription' && collection === 'subscriptions') {
    requestBody = {
      method: method,
      collection: collection,
      params: params
    };
  } else if (method === 'getLabel' && collection === 'labels' && params.id) {
    requestBody = {
      method: method,
      collection: collection,
      params: params.id
    };
  } else if (method === 'getContact' && collection === 'contacts' && params.id) {
    requestBody = {
      method: method,
      collection: collection,
      params: params.id
    };
  } else if (method === 'renameLabel' && collection === 'labels' && params.key) {
    requestBody = {
      method: method,
      collection: collection,
      params: [params.key, params.options]
    };
  } else if (method === 'deleteLabel' && collection === 'labels' && params.id) {
    requestBody = {
      method: method,
      collection: collection,
      params: params.id
    };
  } else if (method === 'deleteLabel' && collection === 'labels' && params.key) {
    requestBody = {
      method: method,
      collection: collection,
      params: params.key
    };
  } else if (collection === 'items' && method === 'queryDataItems') {
    requestBody = {
      method: method,
      collection: collection,
      params: {
        dataCollectionId: params.dataCollectionId,
        consistentRead: params.consistentRead || false,
        returnTotalCount: params.returnTotalCount || false,
        includeReferencedItems: params.includeReferencedItems,
        language: params.language,
        publishPluginOptions: params.publishPluginOptions,
        referencedItemOptions: params.referencedItemOptions,
        appOptions: params.appOptions,
        filter: params.filter,
        sort: params.sort,
        limit: params.limit,
        skip: params.skip,
        dataItemsQuery: true
      }
    };
  } else if (collection === 'items' && (method === 'update' || method === 'insert' || method === 'remove')) {
    requestBody = {
      method: method,
      collection: collection,
      params: {
        dataCollectionId: params.dataCollectionId,
        itemId: params.itemId,
        dataItem: params.dataItem,
        dataItemsOperation: true
      }
    };
  } else if (method.startsWith('query') && collection !== 'items') {
    // Special case: do NOT add queryBuilder for queryBookings on bookings collection
    if (method === 'queryBookings' && collection === 'bookings') {
      requestBody = {
        method: method,
        collection: collection,
        params: { ...params } // just pass filter, sort, limit, etc.
      };
    } else {
      requestBody = {
        method: method,
        collection: collection,
        params: {
          queryBuilder: true,
          ...params
        }
      };
    }
  } else if (method === 'labelContact' && collection === 'contacts') {
    requestBody = {
      method: method,
      collection: collection,
      params: [params.contactId, params.labelKeys]
    };
  } else if (method === 'unlabelContact' && collection === 'contacts') {
    requestBody = {
      method: method,
      collection: collection,
      params: [params.contactId, params.labelKeys]
    };
  } else if (collection === 'inbox' && (method === 'startConversation' || method === 'getConversations')) {
    // Inbox methods need participantIds array
    if (!params.participantIds || !Array.isArray(params.participantIds)) {
      throw new Error('participantIds array is required for inbox ' + method);
    }

    // Don't spread params first - it can overwrite participantIds
    const inboxParams: Record<string, unknown> = {
      participantIds: params.participantIds,
      options: params.options || {}
    };

    // Add any extra params (like firstMessage) but don't overwrite participantIds
    for (const key in params) {
      if (key !== 'participantIds' && key !== 'options') {
        inboxParams[key] = params[key];
      }
    }

    requestBody = {
      method: method,
      collection: collection,
      params: inboxParams
    };
  } else if (collection === 'inbox') {
    // Other inbox methods - pass params as-is
    requestBody = {
      method: method,
      collection: collection,
      params: params
    };
  } else if (collection === 'bookings' && (method === 'createBooking' || method === 'updateBooking' || method === 'createBookingWithAvailabilityCheck' || method === 'updateBookingWithAvailabilityCheck')) {
    // Handle booking methods with optional booking options such as flow control and participant notifications
    const { flowControlSettings, bookingData, options, participantNotification, ...rest } = params;
    if (flowControlSettings || options || participantNotification) {
      requestBody = {
        method: method,
        collection: collection,
        params: {
          bookingData: bookingData ?? rest,
          options: {
            ...(options || {}),
            ...(flowControlSettings ? { flowControlSettings } : {}),
            ...(participantNotification ? { participantNotification } : {})
          }
        }
      };
    } else {
      requestBody = {
        method: method,
        collection: collection,
        params: params
      };
    }
  } else if (method === 'updateContact' && collection === 'contacts') {
    requestBody = {
      method: method,
      collection: collection,
      params: {
        contactId: params.contactId,
        info: params.info,
        revision: params.revision,
        options: params.options || {}
      }
    };
  } else if (method === 'uploadContactAvatar' && collection === 'contacts') {
    requestBody = {
      method: method,
      collection: collection,
      params: {
        contactId: params.contactId,
        base64Data: params.base64Data,
        mimeType: params.mimeType
      }
    };
  } else if (method === 'generateFileUploadUrl' && collection === 'media') {
    requestBody = {
      method: method,
      collection: collection,
      params: {
        mimeType: params.mimeType,
        sizeInBytes: params.sizeInBytes
      }
    };
  } else if (method === 'findOrCreateExtendedField') {
    requestBody = {
      method: 'findOrCreateExtendedField',
      collection: 'extendedFields',
      params: [params.displayName, params.dataType || 'TEXT']
    };
  } else if (method === 'queryExtendedFields') {
    requestBody = {
      method: method,
      collection: collection,
      params: {
        queryBuilder: true,
        ...params
      }
    };
  } else if (collection === 'notifications') {
    requestBody = {
      method: method,
      collection: collection,
      params: params
    };
  } else if (method === 'listAttachments' && collection === 'attachments') {
    requestBody = {
      method: method,
      collection: collection,
      params: {
        contactId: params.contactId,
        paging: params.paging || {}
      }
    };
  } else if (collection === 'inbox') {
    // Inbox collection - pass params as-is
    requestBody = {
      method: method,
      collection: collection,
      params: params
    };
  }

  // Always include Firebase Auth - callable functions handle auth automatically
  const auth = getAuth();
  const user = auth.currentUser;

  // Check session storage for authentication as well
  let isAuthenticated = !!user;
  if (!isAuthenticated) {
    try {
      const sessionData = localStorage.getItem('rsmaids-auth-session');
      if (sessionData) {
        const tokens = JSON.parse(sessionData);
        if (tokens?.user?.email) {
          isAuthenticated = true;
        }
      }
    } catch {
      // Session storage check failed, continue with Firebase Auth only
    }
  }

  // Only throw if the user is expected to be authenticated AND we have no session token
  // The callable functions will validate the session token on the backend
  // Don't throw here - let the backend handle auth validation
  // This prevents false negatives when Firebase hasn't restored from persistence yet

  // Use Firebase Callable Functions instead of HTTP fetch
  // This automatically handles CORS, auth, and preserves Date objects!
  // The function backend validates the session token if Firebase user is not available
  const functions = getFunctions();
  const wixAdminCallable = httpsCallable(functions, 'wixAdmin');

  try {
    const result = await wixAdminCallable(requestBody);

    // Handle void return types
    if (method === 'deleteLabel' && collection === 'labels') {
      return { success: true, method: 'deleteLabel' };
    }

    if (['removeLabel', 'unlabelContact'].includes(method)) {
      return { success: true, method };
    }

    return result.data as AdminApiResponse;
  } catch (error: unknown) {
    console.error(`❌ callAdminApi failed:`, error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`API call failed: ${errorMessage}`);
  }
}

// Add these utility functions at the top
function wixImageToStaticUrl(wixUrl: string): string {
  if (!wixUrl || !wixUrl.startsWith('wix:image://')) {
    return wixUrl;
  }

  const urlParts = wixUrl.replace('wix:image://', '').split('/');
  if (urlParts.length >= 1) {
    const imageId = urlParts[0].split('#')[0];

    const possibleUrls = [
      `https://static.wixstatic.com/media/${imageId}`,
      `https://static.wixstatic.com/media/${imageId}/v1/fill/w_1000,h_1000,al_c,q_80/file.png`,
      `https://static.wixstatic.com/media/${imageId}/v1/fit/w_1000,h_1000/file.png`,
      `https://${process.env.REACT_APP_WIX_DOMAIN || 'static.wixstatic.com'}/media/${imageId}`,
      `https://static.wixstatic.com/shapes/${imageId}`,
    ];

    const convertedUrl = possibleUrls[1];
    return convertedUrl;
  }

  return `https://static.wixstatic.com/media/${wixUrl}`;
}

function convertWixImageUrl(wixUrl: string): string {
  if (!wixUrl) {
    return '';
  }

  if (!wixUrl.startsWith('wix:image://')) {
    return wixUrl;
  }

  const match = wixUrl.match(/^wix:image:\/\/v1\/([^\/#]+)/);
  if (match) {
    const imageId = match[1];
    return `https://static.wixstatic.com/media/${imageId}/v1/fill/w_400,h_400,al_c,q_80/file.jpg`;
  }

  const fallbackId = wixUrl.replace(/^wix:image:\/\//, '').split(/[\/#]/)[1] || '';
  if (fallbackId) {
    return `https://static.wixstatic.com/media/${fallbackId}`;
  }

  return '';
}

function processPhoneToE164(phone: string): string {
  try {
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    } else if (digits.length >= 10) {
      return `+${digits}`;
    }

    throw new Error('Invalid phone number length');
  } catch {
    throw new Error(`Invalid phone format: ${phone}`);
  }
}

async function resolveWixImageUrl(baseUrl: string): Promise<string> {
  if (/\.(jpg|jpeg|png|webp|gif)$/i.test(baseUrl)) {
    return baseUrl;
  }
  const exts = ['.jpg', '.png', '.webp'];
  for (const ext of exts) {
    const testUrl = baseUrl + ext;
    try {
      const res = await fetch(testUrl, { method: 'HEAD' });
      if (res.ok) return testUrl;
    } catch {
    }
  }
  return baseUrl + '.jpg';
}

const SERVICE_TYPE_IMAGES: Record<string, string> = {
  "Window Cleaning": "/assets/images/window-cleaning.jpg",
  "Free Estimate": "/assets/images/free-estimate.jpg",
  "Refrigerator Cleaning": "/assets/images/fridge-cleaning.jpg",
  "Commercial Cleaning": "/assets/images/commercial-cleaning.jpg",
  "Post Construction Cleaning": "/assets/images/post-construction.jpg",
  "Deep Cleaning": "/assets/images/deep-cleaning.jpg",
  "Recurring Cleaning": "/assets/images/recurring-cleaning.jpg",
  "Oven Cleaning": "/assets/images/oven-cleaning.jpg",
  "Move-Out Cleaning": "/assets/images/move-out.jpg",
  "AirBnb Cleaning Service": "/assets/images/airbnb-cleaning.jpg"
};

// Export these utilities
export { wixImageToStaticUrl, convertWixImageUrl, processPhoneToE164, resolveWixImageUrl, SERVICE_TYPE_IMAGES };
